import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { RdSalesAdjustmentService } from './rd-sales-adjustment.service';

@Component({
    selector: 'gtw-rd-sales-adjustment',
    templateUrl: './rd-sales-adjustment.component.html',
    styleUrls: ['./rd-sales-adjustment.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RdSalesAdjustmentComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal title
  modalTitle = "Add Adjustment";

  // Form data
  entity: any = {};
  adjustmentType: any = {};
  sicCode: any = {};
  basket: any = {};
  fdii: any = {};
  adjustmentAmount: string = '';
  adjAmount: string = '';
  comments: string = '';

  // Dropdown data
  entityDetails: any[] = [];
  adjustmentTypeDetails: any[] = [];
  sicCodeDetails: any[] = [];
  basketDetails: any[] = [];
  fdiiDetails: any[] = [];

  // Component state
  isCreate = true;
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  
  // Loading states
  entitiesLoading = false;
  dropdownLoading = false;

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private rdSalesAdjustmentService: RdSalesAdjustmentService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen) || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal) || {};
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    this.globalService = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp) || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = fp;
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    const data = Array.isArray(this.rowData) ? (this.rowData[0] || {}) : this.rowData;
    this.isCreate = !data.ADJ_KEY || data.ADJ_KEY === '' || data.ADJ_KEY === null;
    this.modalTitle = this.isCreate ? 'Add Adjustment' : 'Edit Adjustment';
  }

  init() {
    try {
      const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
      const row = Array.isArray(this.rowData) ? (this.rowData[0] || {}) : (this.rowData || {});
      const hasRowDataOrIsCreate = this.isCreate || (row && Object.keys(row).length > 0);

      if (hasFilterParams && hasRowDataOrIsCreate) {
        this.loadData();
      }
    } catch (e) {
      console.error('Error in RdSalesAdjustment init:', e);
    }
  }

  loadData() {
    if (!this.isCreate) {
      // EDIT MODE: Nie ładuj 3000 rekordów - użyj danych z rowData
      this.setEntityFromRowData();
      this.loadDropdownData(); // Ładuj od razu dropdown data
    } else {
      // CREATE MODE: Ładuj wszystkie entity (ale w tle)
      this.loadEntityDetails();
    }
  }

  setEntityFromRowData() {
    // Ustaw entity z dostępnych danych rowData - natychmiastowo!
    this.entity = {
      KEY: this.rowData.COMBINATION_KEY,
      VALUE: this.extractEntityNameFromRowData()
    };
    this.entityDetails = [this.entity]; // Tylko 1 element dla disabled dropdown
    console.log('Entity set from rowData:', this.entity);
  }

  extractEntityNameFromRowData(): string {
    // Spróbuj różnych pól które mogą zawierać nazwę entity
    return this.rowData.ENTITY_NAME || 
           this.rowData.LE_NAME || 
           this.rowData.ENTITY_DESC ||
           this.rowData.COMBINATION_NAME ||
           `Entity ${this.rowData.COMBINATION_KEY}`;
  }

  loadEntityDetails() {
    // Tylko dla CREATE MODE - ładuj wszystkie entity
    console.log('Loading all entities for create mode...');
    this.entitiesLoading = true;
    
    const params = {
      action_code: 'g9oqag',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.rdSalesAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.entitiesLoading = false;
        if (response && response.jsonObject) {
          this.entityDetails = response.jsonObject;
          console.log(`✅ Loaded ${this.entityDetails.length} entities for create mode`);
        }
      },
      (error: any) => {
        this.entitiesLoading = false;
        console.error('Error loading entity details:', error);
      }
    );
  }

  loadDropdownData() {
    const combinationKey = this.isCreate ? this.entity.KEY : this.rowData.COMBINATION_KEY;
    
    if (!combinationKey) {
      console.warn('No combination key available for loading dropdown data');
      return;
    }
    
    console.log(`Loading dropdown data for combination key: ${combinationKey}`);
    this.dropdownLoading = true;
    
    const params = {
      action_code: 'dbty56',
      cc_key: combinationKey,
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.rdSalesAdjustmentService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.dropdownLoading = false;
        if (response && response.jsonObject) {
          const collection = response.jsonObject;
          
          // Filter SIC codes
          this.sicCodeDetails = collection.filter((item: any) => item.CODE_GROUP === 'SIC_CODE_PER_ME');
          if (this.sicCodeDetails.length === 0) {
            this.sicCodeDetails = collection.filter((item: any) => item.CODE_GROUP === 'SIC_CODE');
          }
          
          // Filter adjustment types
          this.adjustmentTypeDetails = collection.filter((item: any) => item.CODE_GROUP === 'ADJ_TYPE_CODE');
          
          // Filter baskets
          this.basketDetails = collection.filter((item: any) => item.CODE_GROUP === 'SOURCE_CODE_NEW');
          
          // Filter FDII
          this.fdiiDetails = collection.filter((item: any) => item.CODE_GROUP === 'FDII_CODE');
          
          console.log('✅ Dropdown data loaded successfully');
          
          // Set existing values if editing
          if (!this.isCreate) {
            this.setExistingValues();
          }
        }
      },
      (error: any) => {
        this.dropdownLoading = false;
        console.error('Error loading dropdown data:', error);
      }
    );
  }

  setExistingValues() {
    // Set SIC Code - using CODE_SHORT_DESC like AngularJS
    const existingSicCode = this.sicCodeDetails.find(item => item.CODE_SHORT_DESC === this.rowData.SIC_CODE);
    this.sicCode = existingSicCode || {};
    
    // Set Adjustment Type - using CODE_SHORT_DESC like AngularJS
    const existingAdjType = this.adjustmentTypeDetails.find(item => item.CODE_SHORT_DESC === this.rowData.ADJ_TYPE);
    this.adjustmentType = existingAdjType || {};
    
    // Set Basket - using CODE_DESCRIPTION like AngularJS
    const existingBasket = this.basketDetails.find(item => item.CODE_DESCRIPTION === this.rowData.BASKET);
    this.basket = existingBasket || {};
    
    // Set FDII - using CODE_SHORT_DESC like AngularJS
    const existingFdii = this.fdiiDetails.find(item => item.CODE_SHORT_DESC === this.rowData.FDII);
    this.fdii = existingFdii || {};
    
    // Set amounts and comments - show 0 for empty adjAmount like AngularJS
    this.adjustmentAmount = this.rowData.ADJ_AMT || '';
    this.adjAmount = this.rowData.ADJUSTMENT_AMT || '0';  // Show 0 like AngularJS
    this.comments = this.rowData.COMMENTS || '';
  }

  onEntityChange() {
    if (this.entity && this.entity.KEY) {
      this.loadDropdownData();
    }
  }

  settingAdjustmentAmount(event: any) {
    if (this.adjustmentAmount) {
      this.adjustmentAmount = this.adjustmentAmount.replace(/(?!^-)[^0-9\.]/g, '');
      const amount = this.adjustmentAmount;
      
      if (amount.indexOf('.') !== -1) {
        const amountSp = amount.split('.');
        let bPre = amountSp[0];
        let aPre = amountSp[1];
        
        if (bPre && bPre.length > 20) {
          bPre = bPre.substring(0, 19);
        }
        if (aPre && aPre.length > 2) {
          aPre = aPre.substring(0, 2);
        }
        this.adjustmentAmount = bPre + "." + aPre;
      } else if (amount.length > 20) {
        this.adjustmentAmount = amount.substring(0, amount.length - 1);
      }
    }
  }

  settingAdjAmount(event: any) {
    if (this.adjAmount) {
      this.adjAmount = this.adjAmount.replace(/(?!^-)[^0-9\.]/g, '');
      const amount = this.adjAmount;
      
      if (amount.indexOf('.') !== -1) {
        const amountSp = amount.split('.');
        let bPre = amountSp[0];
        let aPre = amountSp[1];
        
        if (bPre && bPre.length > 20) {
          bPre = bPre.substring(0, 19);
        }
        if (aPre && aPre.length > 2) {
          aPre = aPre.substring(0, 2);
        }
        this.adjAmount = bPre + "." + aPre;
      } else if (amount.length > 20) {
        this.adjAmount = amount.substring(0, amount.length - 1);
      }
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Validation
    if (!this.entity || !this.entity.KEY ||
        !this.adjustmentType || !this.adjustmentType.CODE_KEY ||
        !this.sicCode || !this.sicCode.CODE_KEY ||
        !this.basket || !this.basket.CODE_KEY ||
        !this.fdii || !this.fdii.CODE_KEY ||
        !this.adjustmentAmount || this.adjustmentAmount === '' ||
        !this.adjAmount || this.adjAmount === '' ||
        (this.adjustmentType && this.adjustmentType.CODE_SHORT_DESC === 'Other' && (!this.comments || this.comments === ''))) {
      
      this._snackBar.open('Please select all required fields', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    // Prepare save data
    const returnObj: any = {
      ENTITY: this.entity.KEY,
      COMBINATION_KEY: this.entity.KEY,
      ADJ_TYPE_KEY: this.adjustmentType.CODE_KEY,
      SIC_CODE: this.sicCode.CODE_KEY,
      SOURCE_KEY: this.basket.CODE_KEY,
      FDII_KEY: this.fdii.CODE_KEY,
      AMOUNT: this.adjustmentAmount,
      BEAT_PAYMENT: this.adjAmount,
      COMMENTS: this.comments,
      IS_SINGLE: 'S',
      TRANS_TYPE: 'RND_SALES_DETAIL',
      OPERATION_TYPE: this.isCreate ? 'I' : 'U'
    };

    if (!this.isCreate) {
      returnObj.ADJUSTMENT_KEY = this.rowData.ADJ_KEY;
      returnObj.GROUP_KEY = this.rowData.GROUP_KEY;
    }

    const transactionDetails = [returnObj];
    const otherInfo = {
      sso_id: this.USER_SETTINGS.user?.sso_id || '',
      SCREEN_KEY: this.globalService.screen_key,
      tax_year: this.filterParams.tax_year || this.globalService.tax_year,
      scenario: this.filterParams.scenario || this.globalService.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    const lockObj = [{
      combination_key: this.entity.KEY
    }];

    this.rdSalesAdjustmentService.saveRdSalesAdjustment(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      transactionDetails,
      otherInfo,
      lockObj,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('R & D Sales Adjustment has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset() {
    this.entity = {};
    this.adjustmentType = {};
    this.sicCode = {};
    this.basket = {};
    this.fdii = {};
    this.adjustmentAmount = '';
    this.adjAmount = '';
    this.comments = '';
    
    if (this.isCreate) {
      this.loadEntityDetails();
    } else {
      this.loadData();
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  compareByKey(item1: any, item2: any): boolean {
    return item1 && item2 && item1.KEY === item2.KEY;
  }

  compareByCodeKey(item1: any, item2: any): boolean {
    return item1 && item2 && item1.CODE_KEY === item2.CODE_KEY;
  }
}
